import {FC, ReactNode} from 'react'

import {Text, TextVariant} from '@/components/common/content/Text'
import {FormTooltip} from '@/components/common/form/tooltip/FormTooltip'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {ICON_SIZE_MEDIUM} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {Currency} from '@/utils/currency'

type FormSummaryItemBreakdownRowProps = {
  title: string | ReactNode
  price?: number
  priceNote?: string
  description?: string | ReactNode
  numberType?: 'currency' | 'number'
  hasDecimal?: boolean
  currency?: Currency
  hasBulletPoint?: boolean
  tooltipText?: string
}

export const FormSummaryItemBreakdownRow: FC<FormSummaryItemBreakdownRowProps> = ({
  title,
  price,
  priceNote,
  description,
  currency,
  numberType = 'currency',
  hasDecimal = true,
  hasBulletPoint = false,
  tooltipText,
}) => (
  <>
    <div className="row" data-test="FormSummaryItemBreakdownRow-item">
      <div className="title">
        <Text variant={TextVariant.text14} color={theme.palette.text.light}>
          {title}
        </Text>
        {tooltipText && (
          <div className="tooltip">
            <FormTooltip tooltipText={tooltipText} />
          </div>
        )}
      </div>
      <div className="price">
        {typeof price !== 'undefined' ? (
          <Text variant={TextVariant.text14} color={theme.palette.text.light}>
            {price > 0 ? (
              <LocalizedNumberFormat
                currency={currency}
                value={price}
                type={numberType}
                hasDecimal={hasDecimal}
              />
            ) : (
              priceNote || (
                <LocalizedNumberFormat
                  currency={currency}
                  value={price}
                  type={numberType}
                  hasDecimal={hasDecimal}
                />
              )
            )}
          </Text>
        ) : description ? (
          <Text variant={TextVariant.text14} color={theme.palette.text.light} align="right">
            {description}
          </Text>
        ) : null}
      </div>
    </div>

    <style jsx>{`
      .row {
        display: flex;
        justify-content: space-between;
        margin-left: ${!hasBulletPoint ? `calc(${ICON_SIZE_MEDIUM} + ${theme.spacing[2]})` : 0};
      }
      .title {
        display: flex;
        align-items: center;
      }
      .title::before {
        ${hasBulletPoint ? `content: ''` : ''};
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: ${theme.palette.text.light};
        margin: ${theme.spacing[2]};
      }
      .row + .row {
        margin-top: ${theme.spacing[2]};
      }
      .price {
        white-space: nowrap;
        margin-left: ${theme.spacing[3]};
      }
      .tooltip {
        display: flex;
        margin-left: ${theme.spacing[1.5]};
      }
    `}</style>
  </>
)
